import { useEffect } from 'react';

import {
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  makeStyles,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  useMediaQuery,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';

import { useAuthContext } from '../../context/AuthContextProvider';
import {
  getCriteriaEntry,
  getRankingEntry,
  getYearEntry,
} from '../../services/ranking';

const useStyles = makeStyles(() => ({
  container: {
    width: 1000,
    maxWidth: '100%',
  },
  formControl: {
    width: '100%',
  },
  tooltip: {
    fontSize: '12px',
    textAlign: 'center',
  },
}));

const MENU_PROPS = {
  anchorOrigin: {
    vertical: 'bottom',
    horizontal: 'left',
  },
  transformOrigin: {
    vertical: 'top',
    horizontal: 'left',
  },
  getContentAnchorEl: null,
};

const TIME_RANGE_LABELS = {
  yearly: 'Anual',
  consolidated: 'Acumulado até',
};

const getRankingCode = (rankingEntry) =>
  rankingEntry?.type ?? rankingEntry?.code;

export default function RankingFilters({
  state,
  setState,
  hasConsolidated,
  hasYear,
  hasCriteria,
  search,
  setSearch,
  typeRank,
  institutionId,
  onLoadingChange,

  filters: filtersProp,
  data: dataProp,
}) {
  const { auth } = useAuthContext();
  const classes = useStyles();
  const isExtraSmall = useMediaQuery((theme) => theme.breakpoints.down('xs'));

  const filters = filtersProp ?? dataProp;

  const { market, ranking, criteria, year, timeRange } = state;
  const isFiagroMarket = market?.toLowerCase() === 'fiagro';

  const rankingOptions = (
    filters?.[market]?.rankings?.map((r) => ({
      code: getRankingCode(r),
      name: r.name,
    })) ?? []
  ).sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));

  const selectedRankingEntry = getRankingEntry(filters, market, ranking);
  const criteriaOptions = [...(selectedRankingEntry?.criterias ?? [])].sort(
    (a, b) => a.name.localeCompare(b.name, 'pt-BR'),
  );

  const selectedCriteriaEntry = getCriteriaEntry(
    selectedRankingEntry,
    criteria,
  );
  const yearOptions = selectedCriteriaEntry?.years?.map((y) => y.year) ?? [];

  const selectedYearEntry = getYearEntry(selectedCriteriaEntry, year);
  const timeRangeOptions = selectedYearEntry?.timeranges ?? [];

  const isFiltersReady = !!filters;

  useEffect(() => {
    onLoadingChange?.(!filters);
  }, [filters, onLoadingChange]);

  useEffect(() => {
    if (!market || !filters) return;

    const available = filters[market]?.rankings ?? [];
    if (!available.length) return;

    const currentIsValid = available.some((r) => getRankingCode(r) === ranking);
    if (!currentIsValid) {
      setState({
        ranking: getRankingCode(available[0]),
        criteria: undefined,
        year: undefined,
        timeRange: isFiagroMarket ? 'consolidated' : undefined,
      });
    }
  }, [market, filters]);

  useEffect(() => {
    if (!market || !ranking || !filters) return;

    const rankingEntry = getRankingEntry(filters, market, ranking);
    const available = rankingEntry?.criterias ?? [];

    if (!available.length) {
      setState({
        criteria: undefined,
        year: undefined,
        timeRange: isFiagroMarket ? 'consolidated' : undefined,
      });
      return;
    }

    const currentIsValid = available.some((c) => c.code === criteria);
    if (!currentIsValid) {
      setState({
        criteria: available[0].code,
        year: undefined,
        timeRange: isFiagroMarket ? 'consolidated' : undefined,
      });
    }
  }, [market, ranking, filters]);

  useEffect(() => {
    if (!market || !ranking || !criteria || !filters) return;

    const rankingEntry = getRankingEntry(filters, market, ranking);
    const criteriaEntry = getCriteriaEntry(rankingEntry, criteria);

    if (!criteriaEntry) return;

    const available = criteriaEntry.years ?? [];
    if (!available.length) return;

    const yearNum = typeof year === 'string' ? parseInt(year, 10) : year;
    const currentIsValid = available.some(
      (y) => y.year === yearNum || y.year === year,
    );
    if (!currentIsValid) {
      setState({
        year: available[0].year,
        timeRange: isFiagroMarket ? 'consolidated' : undefined,
      });
    }
  }, [market, ranking, criteria, filters]);

  useEffect(() => {
    if (!market || !ranking || !criteria || !year || !filters) return;

    const rankingEntry = getRankingEntry(filters, market, ranking);
    const criteriaEntry = getCriteriaEntry(rankingEntry, criteria);

    if (!criteriaEntry) return;

    const yearEntry = getYearEntry(criteriaEntry, year);

    if (!yearEntry) return;

    const available = yearEntry.timeranges ?? [];
    if (!available.length) return;

    const currentIsValid = available.includes(timeRange);
    if (!currentIsValid) {
      const next = isFiagroMarket ? 'consolidated' : available[0];
      setState({ timeRange: next });
    }
  }, [market, ranking, criteria, year, filters]);

  return (
    <Grid
      container
      spacing={isExtraSmall ? 1 : 4}
      alignItems="center"
      className={classes.container}>
      <Grid item xs={12} sm={6} md={typeRank === 'ranking_general' ? 2 : 4}>
        <FormControl variant="standard" className={classes.formControl}>
          <InputLabel id="rankings_select">Ranking</InputLabel>
          <Select
            labelId="rankings_select"
            disabled={!rankingOptions.length}
            value={isFiltersReady ? ranking ?? '' : ''}
            onChange={(e) =>
              setState({
                ranking: e.target.value,
                criteria: undefined,
                year: undefined,
                timeRange: isFiagroMarket ? 'consolidated' : undefined,
              })
            }
            inputProps={{ 'aria-label': 'ranking' }}
            MenuProps={MENU_PROPS}>
            {rankingOptions.length ? (
              rankingOptions.map(({ name, code }) => (
                <MenuItem key={code} value={code}>
                  {name}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled value="">
                Nenhuma opção disponível
              </MenuItem>
            )}
          </Select>
        </FormControl>
      </Grid>

      {hasCriteria && (
        <Grid item xs={12} sm={6} md={2}>
          <FormControl variant="standard" className={classes.formControl}>
            <InputLabel id="criteria_select">Critério</InputLabel>
            <Select
              labelId="criteria_select"
              disabled={!ranking || !criteriaOptions.length}
              value={isFiltersReady ? criteria ?? '' : ''}
              onChange={(e) =>
                setState({
                  criteria: e.target.value,
                  year: undefined,
                })
              }
              inputProps={{ 'aria-label': 'criterias' }}
              MenuProps={MENU_PROPS}>
              {criteriaOptions.length ? (
                criteriaOptions.map(({ name, code }) => (
                  <MenuItem key={code} value={code}>
                    {name}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled value="">
                  {ranking ? 'Sem dados disponíveis' : 'Selecione um ranking'}
                </MenuItem>
              )}
            </Select>
          </FormControl>
        </Grid>
      )}

      {hasConsolidated && (
        <Grid item xs={12} sm={6} md={2}>
          <FormControl variant="standard" className={classes.formControl}>
            <InputLabel id="view_select">Visão</InputLabel>
            <Select
              labelId="view_select"
              disabled={!year || !timeRangeOptions.length}
              value={isFiltersReady ? timeRange ?? '' : ''}
              onChange={(e) => setState({ timeRange: e.target.value })}
              inputProps={{ 'aria-label': 'time-range' }}
              MenuProps={MENU_PROPS}>
              {timeRangeOptions.length ? (
                timeRangeOptions.map((range) => (
                  <MenuItem key={range} value={range}>
                    {TIME_RANGE_LABELS[range] ?? range}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled value="">
                  Nenhuma opção disponível
                </MenuItem>
              )}
            </Select>
          </FormControl>
        </Grid>
      )}

      {hasYear && (
        <Grid item xs={12} sm={6} md={2}>
          <FormControl variant="standard" className={classes.formControl}>
            <InputLabel id="period_select">Período</InputLabel>
            <Select
              labelId="period_select"
              disabled={!criteria || !yearOptions.length}
              value={isFiltersReady ? year ?? '' : ''}
              onChange={(e) => setState({ year: e.target.value })}
              inputProps={{ 'aria-label': 'year' }}
              MenuProps={MENU_PROPS}>
              {yearOptions.length ? (
                yearOptions.map((yearItem) => (
                  <MenuItem key={yearItem} value={yearItem}>
                    {yearItem}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled value="">
                  Nenhuma opção disponível
                </MenuItem>
              )}
            </Select>
          </FormControl>
        </Grid>
      )}

      {typeRank === 'ranking_general' && (
        <Grid item xs={12} sm={6} md={3}>
          <Tooltip
            disableFocusListener
            disableTouchListener={
              auth?.user?.subscription?.plan?.permissions.rankings_completo
            }
            disableHoverListener={
              auth?.user?.subscription?.plan?.permissions.rankings_completo
            }
            arrow
            title="É necessário obter um plano para utilizar essa funcionalidade"
            placement="top"
            classes={{ tooltip: classes.tooltip }}>
            <TextField
              className={classes.itemInput}
              placeholder="Ex: Uqbar"
              fullWidth
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              label="Busca por nome"
              disabled={
                !auth?.user?.subscription?.plan?.permissions.rankings_completo
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="primary" />
                  </InputAdornment>
                ),
              }}
            />
          </Tooltip>
        </Grid>
      )}
    </Grid>
  );
}
